import React, { useState } from "react";
import { createPortal } from "react-dom";
import FormattedMarkdown from "./FormattedMarkdown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import Swal from "sweetalert2";
import jsPDF from "jspdf";

export default function AiSummaryModal({
  isOpen,
  onClose,
  summaryText,
  loading,
  grievanceData,
  role = "Officer",
}) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!summaryText) return;
    navigator.clipboard.writeText(summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Summary copied to clipboard!",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const handleExportPDF = () => {
    if (!summaryText) return;
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const gid = grievanceData?.grievance_id || "CaseFile";
      const status = grievanceData?.user_grievance_status || grievanceData?.officer_grievance_status || "N/A";
      const category = grievanceData?.llm_category || "N/A";
      const severity = grievanceData?.severity || "N/A";

      // Header Banner
      doc.setFillColor(79, 70, 229); // Indigo 600
      doc.rect(0, 0, 595, 65, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("NCW Complaint Redressal System", 30, 32);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`AI Complaint Lifecycle & Profile Summary (${role} Perspective)`, 30, 48);

      // Metadata Table Card
      doc.setFillColor(245, 243, 255); // Purple 50
      doc.rect(30, 80, 535, 50, "F");
      doc.setDrawColor(221, 214, 254); // Purple 200
      doc.rect(30, 80, 535, 50, "S");

      doc.setTextColor(55, 48, 163); // Indigo 800
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(`Complaint ID: ${gid}`, 40, 98);
      doc.text(`Category: ${category}`, 220, 98);
      doc.text(`Severity: ${severity}`, 430, 98);

      doc.text(`Status: ${status}`, 40, 116);
      doc.text(`Perspective: ${role} Profile`, 220, 116);
      doc.text(`Generated On: ${new Date().toLocaleDateString("en-IN")}`, 430, 116);

      // Body Section
      let y = 155;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);

      // Strip raw markdown headers and emojis for clean PDF line printing
      const sanitizeForPDF = (text) => {
        return text
          .replace(/[\u2018\u2019]/g, "'") // smart single quotes
          .replace(/[\u201C\u201D]/g, '"') // smart double quotes
          .replace(/[\u2013\u2014]/g, '-') // em/en dashes
          .replace(/[\u20B9]/g, 'Rs.')     // Rupee symbol
          .replace(/[^\x00-\xFF]/g, "")    // Remove emojis and non-Latin-1
          .replace(/Grievance/g, "Complaint") // Replace Grievance -> Complaint in AI text
          .replace(/grievance/g, "complaint")
          .trim();
      };
      
      const lines = summaryText.split("\n");

      lines.forEach((rawLine) => {
        const line = rawLine.trim();
        if (!line) {
          y += 6;
          return;
        }

        if (y > 780) {
          doc.addPage();
          y = 40;
        }

        if (line.startsWith("###")) {
          y += 10;
          doc.setFillColor(238, 242, 255);
          doc.rect(30, y - 10, 535, 20, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor(49, 46, 129);
          const headerText = sanitizeForPDF(line.replace(/###\s*/, "").replace(/\*\*/g, ""));
          doc.text(headerText, 35, y + 4);
          y += 18;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.setTextColor(40, 40, 40);
        } else {
          const cleanLine = sanitizeForPDF(line.replace(/\*\*/g, "").replace(/`/g, ""));
          const wrapped = doc.splitTextToSize(cleanLine, 525);
          wrapped.forEach((wrappedLine) => {
            if (y > 780) {
              doc.addPage();
              y = 40;
            }
            doc.text(wrappedLine, 35, y);
            y += 13;
          });
        }
      });

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text("Official AI Generated Case Summary — NCW Complaint Redressal System", 30, 820);

      doc.save(`AI_Summary_${gid}.pdf`);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "PDF exported successfully!",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (err) {
      console.error("Error generating PDF:", err);
      Swal.fire("Error", "Could not export PDF summary.", "error");
    }
  };

  const getStatusColor = (statusStr) => {
    const s = (statusStr || "").toLowerCase();
    if (s.includes("resolve") || s.includes("closed"))
      return "bg-emerald-100 text-emerald-800 border-emerald-300";
    if (s.includes("progress") || s.includes("respond") || s.includes("attach"))
      return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-blue-100 text-blue-800 border-blue-300";
  };

  const getSeverityColor = (sevStr) => {
    const s = (sevStr || "").toLowerCase();
    if (s.includes("critical") || s.includes("high"))
      return "bg-red-100 text-red-700 border-red-300";
    if (s.includes("semi") || s.includes("medium"))
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    return "bg-gray-100 text-gray-700 border-gray-300";
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity animate-fadeIn">
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-purple-100 max-h-[90vh] flex flex-col">
        {/* Header Bar */}
        <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 px-6 py-5 text-white flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl shadow-inner">
              <AutoAwesomeIcon className="text-yellow-300 animate-pulse text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-wide flex items-center gap-2">
                AI Complaint Summary
              </h2>
              <p className="text-xs text-purple-200 font-medium">
                Structured Profile Summary ({role} Perspective)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            title="Close"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Metadata Bar */}
        {grievanceData && (
          <div className="bg-purple-50/70 border-b border-purple-100 px-6 py-3 flex flex-wrap items-center gap-3 text-xs">
            <span className="font-mono bg-white px-2.5 py-1 rounded-md border border-purple-200 text-purple-900 font-semibold shadow-sm">
              ID: {grievanceData.grievance_id || "N/A"}
            </span>

            <span className="bg-indigo-100 text-indigo-900 px-2.5 py-1 rounded-md font-semibold border border-indigo-200">
              Role: {role} View
            </span>

            {grievanceData.llm_category && (
              <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-md font-medium border border-purple-200">
                {grievanceData.llm_category}
              </span>
            )}

            {(grievanceData.user_grievance_status || grievanceData.officer_grievance_status) && (
              <span
                className={`px-2.5 py-1 rounded-md font-medium border ${getStatusColor(
                  grievanceData.user_grievance_status || grievanceData.officer_grievance_status
                )}`}
              >
                Status: {grievanceData.user_grievance_status || grievanceData.officer_grievance_status}
              </span>
            )}

            {grievanceData.severity && (
              <span
                className={`px-2.5 py-1 rounded-md font-medium border ${getSeverityColor(
                  grievanceData.severity
                )}`}
              >
                Severity: {grievanceData.severity}
              </span>
            )}
          </div>
        )}

        {/* Body Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4 font-sans text-gray-800 leading-relaxed text-sm bg-gradient-to-b from-white via-gray-50/30 to-white">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center space-y-4">
              <div className="relative flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                <AutoAwesomeIcon className="absolute text-purple-600 animate-bounce" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-purple-900">
                  Generating {role} Profile Summary...
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Synthesizing case lifecycle, status updates, ATR, and role-specific action items
                </p>
              </div>
            </div>
          ) : summaryText ? (
            <FormattedMarkdown content={summaryText} />
          ) : (
            <div className="py-12 text-center text-gray-500">
              <p className="text-base">No summary content generated.</p>
            </div>
          )}
        </div>

        {/* Footer Bar */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={loading || !summaryText}
              className="px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
            >
              {copied ? (
                <>
                  <CheckIcon fontSize="small" className="text-emerald-600" />
                  Copied!
                </>
              ) : (
                <>
                  <ContentCopyIcon fontSize="small" />
                  Copy Summary
                </>
              )}
            </button>

            <button
              onClick={handleExportPDF}
              disabled={loading || !summaryText}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-all disabled:opacity-50 shadow-sm"
            >
              <PictureAsPdfIcon fontSize="small" />
              Export PDF
            </button>
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-xl text-xs font-medium shadow transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
